/**
 * Pure helpers for the Experiments list empty state (Paper → Experiments →
 * "Experiments · No tests yet").
 */
import type { BusiestPage } from "./ExperimentsListStates"

/** "this week" for a 7-day window, so "7,280 visitors this week" reads naturally. */
export function periodWord(period: string | undefined): string {
  switch (period) {
    case "1d":
    case "PAST_24_H":
      return "in the last 24 hours"
    case "7d":
    case "LAST_7_DAYS":
      return "this week"
    case "mtd":
    case "MONTH_TO_DATE":
      return "this month"
    case "ytd":
    case "YEAR_TO_DATE":
      return "this year"
    default:
      return ""
  }
}

export function cleanDomain(domain: string | undefined | null): string {
  return (domain || "").replace(/^https?:\/\//, "").replace(/^www\./, "").replace(/\/$/, "")
}

/** "acme.com · 7,280 visitors this week, Sep 19 to Sep 25" */
export function noTestsSubtitle({
  domain,
  visitors,
  period,
  range,
}: {
  domain: string
  visitors: number
  period?: string
  range: string | null
}): string {
  const word = periodWord(period)
  const traffic =
    visitors > 0
      ? `${visitors.toLocaleString("en-US")} visitors${word ? ` ${word}` : ""}${range ? `, ${range}` : ""}`
      : range
  return [domain, traffic].filter(Boolean).join(" · ")
}

/**
 * The busiest page card, from the period's page rows (`metrics.pageSessions`,
 * unique visitors per page). The share divides by the period's deduped total,
 * never a row sum. Null when there is no page traffic.
 */
export function buildBusiestPage({
  pages,
  totalVisitors,
  domain,
  period,
}: {
  pages: Array<{ page?: string; sessions?: number; count?: number }> | null | undefined
  totalVisitors: number
  domain: string
  period?: string
}): (BusiestPage & { path: string }) | null {
  const top = (pages ?? [])
    .map((p) => ({ path: p.page || "", visitors: Number(p.sessions ?? p.count ?? 0) || 0 }))
    .filter((p) => p.path && p.visitors > 0)
    .sort((a, b) => b.visitors - a.visitors)[0]
  if (!top) return null
  const path = top.path.startsWith("/") ? top.path : `/${top.path}`
  const word = periodWord(period)
  const share = totalVisitors > 0 ? Math.round((top.visitors / totalVisitors) * 100) : null
  return {
    path,
    label: `${domain}${path}`,
    detail: [
      "Your busiest page",
      `${top.visitors.toLocaleString("en-US")} visitors${word ? ` ${word}` : ""}`,
      share != null && share <= 100 ? `${share}% of visitors` : null,
    ]
      .filter(Boolean)
      .join(" · "),
  }
}
